import React, { useState, useEffect, useCallback } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { StudentFullInfoModal } from './StudentFullInfoModal';
import FilterSortPanel from '../../../ui/FilterSortPanel';
import StudentExamsModal from '../../StudentList/StudentExamsModal';
import { copyToClipboard } from '../../../../utils/clipboard';
import { useToast } from '../../../../contexts/ToastContext';

interface StudentDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  applicationId: string;
  onLogout: () => void;
}

interface NestedProgram {
  id: number;
  name: string;
  duration_hours?: number | null;
  is_active: boolean;
}

interface NestedStudentProgram {
  student_id: number;
  program_id: number;
  status: string;
  start_date: string;
  end_date?: string | null;
  exam_date?: string | null;
  program: NestedProgram;
}

interface NestedStudent {
  id: number;
  application_id: number;
  full_name: string;
  email: string | null;
  login: string | null;
  password: string | null;
  position: string | null;
  is_active: boolean;
  created_at?: string;
  student_programs: NestedStudentProgram[];
}

interface OrganizationRead {
  id: number;
  full_name: string;
  short_name?: string | null;
}

interface ResponsibleSpecialistRead {
    id: number;
    full_name: string;
}

interface ApplicationReadResponse {
  id: number;
  organization_id: number;
  status: string;
  students: NestedStudent[];
  organization: OrganizationRead | null;
  responsible_specialist: ResponsibleSpecialistRead | null;
}

const StudentDetailsModal: React.FC<StudentDetailsModalProps> = ({
  isOpen,
  onClose,
  applicationId,
  onLogout,
}) => {
  const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api/v1`;

  const { addToast } = useToast();

  // Удален стейт copiedStudentId
  const [applicationData, setApplicationData] = useState<ApplicationReadResponse | null>(null);
  const [organizationName, setOrganizationName] = useState<string>('');
  const [loadingApp, setLoadingApp] = useState<boolean>(false);
  const [selectedStudentForFullInfo, setSelectedStudentForFullInfo] = useState<NestedStudent | null>(null);
  const [selectedStudentForExam, setSelectedStudentForExam] = useState<NestedStudent | null>(null);
  const [isExamModalOpen, setIsExamModalOpen] = useState<boolean>(false);

  const [studentsList, setStudentsList] = useState<NestedStudent[]>([]);
  const [loadingStudents, setLoadingStudents] = useState<boolean>(false);
  const [studentQuery, setStudentQuery] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setSelectedStudentForFullInfo(null);
      setSelectedStudentForExam(null);
      setIsExamModalOpen(false);
      setStudentQuery('');
      setStudentsList([]);
      setApplicationData(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !applicationId) return;

    const fetchApplication = async () => {
      const token = localStorage.getItem('token');
      setLoadingApp(true);
      try {
        const appRes = await fetch(`${API_BASE_URL}/applications/${applicationId}`, {
            headers: {
                "Authorization": token || ""
            }
        });

        if (appRes.status === 401) {
            onLogout();
            return;
        }

        if (!appRes.ok) throw new Error('Failed to fetch application');
        const appData: ApplicationReadResponse = await appRes.json();

        setApplicationData(appData);
        if (appData.organization) {
          setOrganizationName(appData.organization.short_name || appData.organization.full_name);
        } else {
          setOrganizationName('—');
        }
      } catch (error) {
        console.error('Error loading application:', error);
        setOrganizationName('Ошибка загрузки');
      } finally {
        setLoadingApp(false);
      }
    };

    fetchApplication();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, applicationId]);

  const fetchStudents = useCallback(async () => {
    if (!isOpen || !applicationId) return;

    const token = localStorage.getItem('token');
    setLoadingStudents(true);
    try {
      const requestBody = {
        application_id: Number(applicationId),
        organization_name: null,
        student_query: studentQuery || null,
        start_date_from: null,
        start_date_to: null,
        end_date_from: null,
        end_date_to: null,
        sort_by: 'created_at',
        sort_order: 'desc'
      };

      const PAGE_SIZE = 200;
      const MAX_PAGES = 50;
      const allStudents: NestedStudent[] = [];
      let page = 1;
      let total = Infinity;

      while (page <= MAX_PAGES && allStudents.length < total) {
        const res = await fetch(
          `${API_BASE_URL}/students/search?page=${page}&size=${PAGE_SIZE}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: token || '',
            },
            body: JSON.stringify(requestBody),
          }
        );

        if (res.status === 401) {
          onLogout();
          return;
        }

        if (!res.ok) throw new Error('Failed to search students');

        const data = await res.json();

        if (Array.isArray(data)) {
          allStudents.push(...data);
          break;
        }

        const items: NestedStudent[] = data.items || [];
        allStudents.push(...items);
        total = typeof data.total === 'number' ? data.total : allStudents.length;

        if (items.length === 0) break;
        page += 1;
      }

      setStudentsList(allStudents);
    } catch (error) {
      console.error('Error searching students:', error);
      setStudentsList([]);
    } finally {
      setLoadingStudents(false);
    }
  }, [isOpen, applicationId, studentQuery, onLogout]);

  useEffect(() => {
    if (isOpen && applicationId) {
      fetchStudents();
    }
  }, [isOpen, applicationId]);

  // Упростили сигнатуру (studentId больше не нужен)
  const handleCopyCredentials = async (login: string, pass: string) => {
    const textToCopy = `Логин: ${login}\nПароль: ${pass}`;

    const success = await copyToClipboard(textToCopy);

    if (success) {
      addToast({
        message: 'Логин и пароль успешно скопированы!',
        type: 'success'
      });
    } else {
      addToast({
        message: 'Не удалось скопировать данные',
        type: 'error'
      });
    }
  };

  const formatDate = (isoString?: string) => {
    if (!isoString) return '—';
    try { return new Date(isoString).toLocaleDateString('ru-RU'); } catch { return isoString; }
  };

  const formatDatesList = (dates: (string | null | undefined)[]) => {
      const validDates = dates.filter((d): d is string => !!d);
      if (validDates.length === 0) return "—";
      const uniqueDates = Array.from(new Set(validDates));
      uniqueDates.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
      return uniqueDates.map(d => formatDate(d)).join("; ");
  };

  const handleOpenExamModal = (student: NestedStudent) => {
      setSelectedStudentForExam(student);
      setIsExamModalOpen(true);
  };

  const handleCloseExamModal = () => {
      setIsExamModalOpen(false);
      setSelectedStudentForExam(null);
  };

  if (!isOpen) return null;

  if (selectedStudentForFullInfo && applicationData) {
      return (
          <StudentFullInfoModal
              student={selectedStudentForFullInfo}
              applicationContext={{
                  id: applicationData.id,
                  status: applicationData.status,
                  organization: { name: organizationName },
                  specialist: { name: applicationData.responsible_specialist?.full_name || 'Не назначен' }
              }}
              onBack={() => setSelectedStudentForFullInfo(null)}
          />
      );
  }

  if (loadingApp && !applicationData) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white p-8 rounded-xl shadow-2xl flex flex-col items-center">
             <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
             <p className="text-gray-600">Загрузка заявки...</p>
          </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-[95%] h-[95%] md:w-[80%] md:h-[90%] flex flex-col overflow-hidden border border-gray-200">

        {/* Заголовок */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gray-50 shrink-0">
          <div>
            <h2 className="text-xl font-bold text-gray-800">Список студентов</h2>
            <p className="text-sm text-gray-500">
                Заявка ID: {applicationId} {organizationName && `• ${organizationName}`}
                {!loadingStudents && ` • Студентов: ${studentsList.length}`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full text-gray-400 hover:bg-gray-200 hover:text-gray-700 transition focus:outline-none"
          >
            <CloseIcon />
          </button>
        </div>

        <FilterSortPanel
            searchTerm={studentQuery}
            onSearchChange={setStudentQuery}
            searchPlaceholder="Имя студента или логин"
            onSearchSubmit={fetchStudents}
        />

        {/* Тело списка */}
        <div className="flex-1 overflow-auto p-6 bg-gray-100 relative">

          {loadingStudents && (
             <div className="absolute inset-0 bg-white/60 z-10 flex justify-center pt-20">
                 <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
             </div>
          )}

          {studentsList.length === 0 && !loadingStudents ? (
            <div className="h-full flex flex-col items-center justify-center text-gray-500">
              <p className="text-lg">Студенты не найдены по заданным критериям.</p>
              {studentQuery && (
                <button
                    onClick={() => {
                        setStudentQuery('');
                        setTimeout(fetchStudents, 0);
                    }}
                    className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition"
                >
                    Сбросить поиск
                </button>
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {studentsList.map((student) => {
                const myPrograms = student.student_programs || [];

                const allStartDates = myPrograms.map(p => p.start_date);
                const allEndDates = myPrograms.map(p => p.end_date);

                const startDateDisplay = formatDatesList(allStartDates);
                const endDateDisplay = formatDatesList(allEndDates);

                return (
                  <div
                    key={student.id}
                    className="relative bg-white rounded-lg shadow-sm border border-gray-200 border-l-4 border-l-blue-500 p-5 hover:shadow-md transition-shadow"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1 pr-4">
                        <h3 className="text-lg font-bold text-gray-800 mb-1">
                          {student.full_name || 'Без имени'}
                        </h3>

                        <div className="text-sm text-gray-600 mb-3">
                            {student.email ? (
                                <span className="text-gray-600">Email: {student.email}</span>
                            ) : (
                                <span className="text-gray-400 italic">Email не указан</span>
                            )}
                        </div>

                        <div className="text-sm text-gray-600 space-y-1.5">
                          <div className="flex gap-2">
                            <span className="text-gray-500 w-44 shrink-0">Дата начала обучения:</span>
                            <span className="font-medium">{startDateDisplay}</span>
                          </div>

                          <div className="flex gap-2">
                            <span className="text-gray-500 w-44 shrink-0">Дата окончания обучения:</span>
                            <span className="font-medium">{endDateDisplay}</span>
                          </div>

                          {student.created_at && (
                            <div className="flex gap-2">
                                <span className="text-gray-500 w-44 shrink-0">Дата создания:</span>
                                <span className="font-medium text-gray-400 text-xs pt-0.5">{formatDate(student.created_at)}</span>
                            </div>
                          )}

                          <div className="flex flex-col gap-1.5 my-2">
                              <div className="flex items-center gap-2">
                                <span className="text-gray-500 w-44 shrink-0">Логин:</span>
                                <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-blue-700 select-all">
                                  {student.login || '—'}
                                </span>

                                {student.login && student.password && (
                                  <button
                                    onClick={() => handleCopyCredentials(student.login!, student.password!)}
                                    className="ml-1 text-gray-400 hover:text-blue-600 transition flex items-center justify-center bg-gray-50 hover:bg-blue-50 px-2 py-1 rounded border border-transparent hover:border-blue-100"
                                    title="Скопировать логин и пароль"
                                  >
                                      {/* Кнопка теперь всегда показывает только иконку копирования */}
                                      <ContentCopyIcon style={{ fontSize: 16 }} />
                                  </button>
                                )}
                              </div>

                              <div className="flex items-center gap-2">
                                <span className="text-gray-500 w-44 shrink-0">Пароль:</span>
                                <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-red-700 select-all">
                                  {student.password || '—'}
                                </span>
                              </div>
                          </div>

                          <div className="flex gap-2 mt-2">
                            <span className="text-gray-500 w-44 shrink-0">Организация:</span>
                            <span className="font-medium text-gray-800">
                                {organizationName || '—'}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="shrink-0 flex border border-gray-300 rounded-md overflow-hidden bg-white h-9">
                        <button
                            onClick={() => setSelectedStudentForFullInfo(student)}
                            className="px-3 flex items-center justify-center text-blue-600 hover:bg-blue-50 border-r border-gray-300 transition"
                            title="Просмотр полной информации"
                        >
                          <VisibilityIcon fontSize="small" />
                        </button>
                        <button
                            className="px-3 flex items-center justify-center text-green-600 hover:bg-green-50 transition"
                            title="Экзамен"
                            onClick={() => handleOpenExamModal(student)}
                        >
                          <CheckCircleOutlineIcon fontSize="small" />
                        </button>
                      </div>
                    </div>

                    <div className="mt-4 pt-3 border-t border-dashed border-gray-200">
                        <span className="text-xs text-gray-400 block mb-2 uppercase font-semibold tracking-wider">
                            Подключенные программы:
                        </span>

                        <div className="flex flex-wrap gap-2">
                            {myPrograms.length > 0 ? (
                                myPrograms.map((sp, idx) => {
                                    const programName = sp.program?.name || `Программа #${sp.program_id}`;
                                    return (
                                        <div key={idx} className="bg-blue-50 text-blue-800 px-3 py-1 rounded-full text-xs border border-blue-100">
                                            {programName}
                                        </div>
                                    );
                                })
                            ) : (
                                <span className="text-xs text-gray-400 italic">Нет подключенных программ</span>
                            )}
                        </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end shrink-0">
            <button
                onClick={onClose}
                className="px-6 py-2 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 font-medium shadow-sm transition"
            >
                Закрыть
            </button>
        </div>
      </div>

      <StudentExamsModal
          isOpen={isExamModalOpen}
          onClose={handleCloseExamModal}
          student={selectedStudentForExam}
          onLogout={onLogout}
      />
    </div>
  );
};

export default StudentDetailsModal;